/**
 * Number and time inputs whose box always shows what is actually saved.
 *
 * The plain pattern — `value={num}` + `onChange={e => save(Number(e.target.value || dflt))}`
 * — writes a fallback the instant the box is emptied, and that fallback lands
 * straight back in the box. With a fallback of 0 the user types "200" and sees
 * "0200"; with a fallback of 14 they backspace the 14, type "20" and SAVE 1420.
 *
 * Here the box keeps the user's own text while they edit. Only a complete,
 * finite number is ever written through; an empty or half-typed box ("", "-",
 * "1e") writes nothing and, on blur, snaps back to the saved value — so what is
 * on screen and what is saved can only differ while the caret is in the box,
 * and `required` stops a submit with an empty box. Leading zeros are dropped as
 * they are typed ("02" → "2"), and focusing selects the contents so typing
 * replaces the number instead of appending to it.
 *
 * It stays `type="number"`, so the native min / max / step checks on submit and
 * the arrow-key stepping are exactly what they were. (Mouse-wheel changes on a
 * focused box are blocked globally in main.tsx.)
 */
import { useEffect, useState } from "react";

type NativeProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type"
>;

interface NumberInputProps extends NativeProps {
  value: number;
  onChange: (value: number) => void;
}

const fmt = (v: number) => (Number.isFinite(v) ? String(v) : "");

/** The number a box's text stands for, or null when it is not a complete one. */
function parse(text: string): number | null {
  if (text.trim() === "") return null;
  const v = Number(text);
  return Number.isFinite(v) ? v : null;
}

export function NumberInput({
  value,
  onChange,
  onFocus,
  onBlur,
  min,
  step,
  inputMode,
  required = true,
  ...rest
}: NumberInputProps) {
  const [draft, setDraft] = useState(() => fmt(value));

  // Follow outside changes (the JSON box, a strategy switch) — but leave the
  // user's own spelling alone when it already means the saved number, or
  // "2." / "0.50" would be rewritten under the caret.
  useEffect(() => {
    setDraft((d) => (parse(d) === value ? d : fmt(value)));
  }, [value]);

  // A numeric keypad where it cannot cost anything: iOS's decimal pad has no
  // minus key, so a box that accepts negatives keeps the default keyboard.
  const allowsNegative = min === undefined || Number(min) < 0;
  const isInteger = step === undefined || (step !== "any" && Number.isInteger(Number(step)));
  const mode = inputMode ?? (allowsNegative ? undefined : isInteger ? "numeric" : "decimal");

  return (
    <input
      {...rest}
      type="number"
      min={min}
      step={step}
      inputMode={mode}
      required={required}
      value={draft}
      onChange={(e) => {
        const text = e.target.value.replace(/^(-?)0+(?=\d)/, "$1");
        setDraft(text);
        const v = parse(text);
        if (v !== null && v !== value) onChange(v);
      }}
      onFocus={(e) => {
        // Deferred: selecting inside the focus event is undone by the mouseup
        // of the same click in Safari and Chrome.
        const el = e.target;
        requestAnimationFrame(() => {
          if (document.activeElement === el) el.select();
        });
        onFocus?.(e);
      }}
      onBlur={(e) => {
        // Whatever was left in the box, show exactly what is saved.
        setDraft(fmt(value));
        onBlur?.(e);
      }}
    />
  );
}

interface TimeInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> {
  value: string;
  onChange: (value: string) => void;
}

/**
 * `<input type="time">` that never writes an empty time (the strategy rejects
 * one) but still lets the user clear a segment to retype it: the empty state is
 * kept locally, `required` blocks a submit, and blur restores the saved time.
 * The old version swallowed the empty change outright, which made Backspace
 * look dead and the iOS picker's "Clear" do nothing.
 */
export function TimeInput({ value, onChange, onBlur, ...rest }: TimeInputProps) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <input
      {...rest}
      type="time"
      required
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        if (e.target.value) onChange(e.target.value);
      }}
      onBlur={(e) => {
        setDraft(value);
        onBlur?.(e);
      }}
    />
  );
}

/**
 * iOS "Smart Punctuation" turns a typed " into “ or ”, which is never valid
 * JSON. Straightens them in place — same length, so the caret is restored where
 * it was — and returns the fixed text for the caller's state setter.
 */
export function straightenJsonQuotes(
  el: HTMLInputElement | HTMLTextAreaElement,
): string {
  const fixed = el.value.replace(/[“”„‟]/g, '"');
  if (fixed !== el.value) {
    const { selectionStart, selectionEnd } = el;
    el.value = fixed;
    if (selectionStart !== null && selectionEnd !== null) {
      el.setSelectionRange(selectionStart, selectionEnd);
    }
  }
  return fixed;
}

/** Spread onto any JSON / symbol text box: no autocorrect, capitalisation or
 *  spell-check rewriting what the user typed. */
export const noAutocorrect = {
  autoCorrect: "off",
  autoCapitalize: "off",
  spellCheck: false,
} as const;
